import React, { useCallback, useEffect, useRef, useState } from 'react'
import { get } from 'lodash'
import cognito from 'shared/cognito'
import { useIsMounted } from './EsignWebsocket'
import PropTypes from 'prop-types'

const { REACT_APP_WEBSOCKET_BOSSWEB } = process.env

const closeSocket = (code, reason, webSocketRef) => {
  if (
    webSocketRef.current &&
    webSocketRef.current.readyState === WebSocket.OPEN
  ) {
    webSocketRef.current.close(code, reason)
  }
}

const VerificationWebsocket = ({ applicationNumber, setVerificationStatus }) => {
  const webSocketRef = useRef(WebSocket)
  const isMounted = useIsMounted()
  const [isConnectionOpened, setIsConnectionOpened] = useState(false)
  const serverURL = `${REACT_APP_WEBSOCKET_BOSSWEB}?Auth=${cognito.idToken}`

  const registerVerification = useCallback((applicationNumber, websocketRef) => {
    if (websocketRef.current) {
      websocketRef.current.send(
        JSON.stringify({
          action: 'registerListener',
          loanId: applicationNumber
        })
      )
    }
  }, [])

  useEffect(() => {
    const connectOrReconnect = () => {
      if (
        !webSocketRef.current ||
        webSocketRef.current.CLOSED === 3 ||
        webSocketRef.current.readyState === WebSocket.CLOSED
      ) {
        const webSocket = new WebSocket(serverURL)
        webSocketRef.current = webSocket
      }
    }

    if (!isConnectionOpened) {
      connectOrReconnect()
    }

    if (webSocketRef.current) {
      webSocketRef.current.onopen = () => {
        // ensure it is ready and the component is mounted to prevent memory leak
        if (webSocketRef.current.readyState === 1 && isMounted.current) {
          setIsConnectionOpened(true)
          registerVerification(applicationNumber, webSocketRef)
        }
      }

      webSocketRef.current.onerror = () => {
        setIsConnectionOpened(false)
        setVerificationStatus('ERROR')
      }

      webSocketRef.current.onclose = event => {
        if (event.code !== 1000) {
          connectOrReconnect()
        } else {
          setIsConnectionOpened(false)
        }
      }

      webSocketRef.current.onmessage = msg => {
        const fullMessage = JSON.parse(msg.data)
        const messageData = get(fullMessage, 'data.message.body')
        if (messageData) {
          const status = get(messageData, 'status', null)

          if (
            status
          ) {
            closeSocket(
              1000,
              'Disconnecting websocket, result found',
              webSocketRef
            )
            console.log(status, '<-- setting verification status')
            setVerificationStatus(status)
          }
        }
      }
      // Must return a function - linter
      return () => false
    }

    // prevents memory leak when the page is refreshed during a connection
    return () => {
      closeSocket(1000, 'no longer needed', webSocketRef)
      isMounted.current = false
    }

  }, [
    applicationNumber,
    isConnectionOpened,
    registerVerification,
    serverURL,
    setVerificationStatus,
    isMounted,
  ])

  return <React.Fragment />
}

VerificationWebsocket.propTypes = {
  applicationNumber: PropTypes.string.isRequired,
  setVerificationStatus: PropTypes.func.isRequired,
}

export default VerificationWebsocket
